import { ArrowDown, ArrowUp } from '@sds-system/assets';
import { allowScroll, preventScroll } from '@sds-system/utils';
import type { ChangeEvent, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import { Checkbox } from '../Checkbox';
import { Typography } from '../Typography';
import {
  ComboBoxContainer,
  ComboBoxRootColumn,
  DropdownContent,
  DropdownWrapper,
} from './ComboBox.styled';
import type { ComboBoxProps } from './ComboBox.types';
import { ComboBoxPortal } from './ComboBoxPortal';

export const ComboBox = ({
  size,
  defaultValue,
  onChange,
  list,
  isAll = true,
  styledComponents,
  width = '100%',
  errorMessage,
  disabled,
  isReset,
}: ComboBoxProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [checkedList, setCheckedList] = useState(defaultValue || []);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState(false);

  const handleChangeComboBox = (e: ChangeEvent<HTMLInputElement>) => {
    const checkedValue = e.target.value;
    const checked = e.target.checked;

    if (checkedValue === 'all') {
      if (isAllChecked) {
        setCheckedList([]);
      } else {
        setCheckedList(list.map((item) => item.value));
      }
    } else {
      if (checked) {
        setCheckedList([...checkedList, checkedValue]);
      } else {
        setCheckedList(checkedList.filter((item) => item !== checkedValue));
      }
    }
  };

  const checkedListTrigger = () => {
    if (checkedList.length === 0) {
      return '선택';
    }

    const optionLabel = list.find((data) => data.value === checkedList[0])?.label;

    if (checkedList.length === 1) {
      return optionLabel;
    }

    if (checkedList.length === list.length) {
      return '전체';
    }

    return `${optionLabel} +${checkedList.length - 1}`;
  };

  const handleClickOutside = (e: unknown) => {
    const event = (e as React.MouseEvent<HTMLElement>).target as HTMLElement;

    if (typeof event.className.includes === 'undefined') {
      if (event.tagName === 'path' || event.tagName === 'svg') {
        return;
      } else {
        setIsOpenDropdown(false);
        return;
      }
    }

    if (typeof event.className.includes !== 'undefined' && event.className.includes('dropdown')) {
      return;
    } else {
      setIsOpenDropdown(false);
    }
  };

  const getContainerRect = () => {
    if (!ref.current) return;

    const { left, bottom, width } = ref.current.getBoundingClientRect();

    return {
      left,
      top: bottom + 4,
      minWidth: width,
    };
  };

  useEffect(() => {
    if (checkedList.length === list.length) {
      setIsAllChecked(true);
    } else {
      setIsAllChecked(false);
    }

    onChange(checkedList);
  }, [checkedList]);

  useEffect(() => {
    onChange(checkedList, isAllChecked);
  }, [isAllChecked]);

  useEffect(() => {
    if (isReset) {
      setCheckedList([]);
    }
  }, [isReset]);

  useEffect(() => {
    if (isOpenDropdown) {
      const prevScrollY = preventScroll();
      return () => {
        allowScroll(prevScrollY);
      };
    }
    return;
  }, [isOpenDropdown]);

  useOnClickOutside(ref, handleClickOutside, 'mouseup');

  return (
    <ComboBoxRootColumn gap={8} {...styledComponents?.root}>
      <ComboBoxContainer
        alignItems='center'
        gap={4}
        justifyContent='space-between'
        onClick={() => !disabled && setIsOpenDropdown((prev) => !prev)}
        ref={ref}
        size={size}
        {...styledComponents?.trigger}
        disabled={disabled}
        errorMessage={errorMessage}
        width={width}
      >
        {checkedListTrigger()}
        {isOpenDropdown ? <ArrowUp /> : <ArrowDown />}
        {isOpenDropdown && (
          <ComboBoxPortal id='dropdown'>
            <DropdownWrapper
              onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              {...styledComponents?.wrapper}
              className='dropdown-wrapper'
              css={getContainerRect()}
            >
              {isAll && (
                <DropdownContent
                  alignItems='center'
                  checked={isAllChecked}
                  gap={8}
                  size={size}
                  {...styledComponents?.content}
                  className='dropdown-content'
                >
                  <Checkbox
                    checked={isAllChecked}
                    className='dropdown-checkbox'
                    onChange={handleChangeComboBox}
                    value={'all'}
                  />
                  전체
                </DropdownContent>
              )}
              {list.map((item) => (
                <DropdownContent
                  alignItems='center'
                  checked={!!checkedList.find((data) => data === item.value)}
                  gap={8}
                  key={item.value}
                  size={size}
                  {...styledComponents?.content}
                  className='dropdown-content'
                >
                  <Checkbox
                    checked={!!checkedList.find((data) => data === item.value)}
                    className='dropdown-checkbox'
                    onChange={handleChangeComboBox}
                    value={item.value}
                  />
                  {item.label}
                </DropdownContent>
              ))}
            </DropdownWrapper>
          </ComboBoxPortal>
        )}
      </ComboBoxContainer>
      {errorMessage && (
        <Typography color='text.error' fontType='caption.regular'>
          {errorMessage}
        </Typography>
      )}
    </ComboBoxRootColumn>
  );
};
